import React from 'react';
import {motion} from 'framer-motion';
import {useNavigate} from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiTarget, FiHeart, FiUsers, FiTrendingUp, FiCode, FiBrain, FiZap} = FiIcons;

const About = () => {
  const navigate = useNavigate();

  const handleStartProject = () => {
    navigate('/contact');
  };

  const handleLearnMore = () => {
    navigate('/services');
  };

  const fadeInUp = {
    initial: {opacity: 0, y: 60},
    animate: {opacity: 1, y: 0},
    transition: {duration: 0.6}
  };

  const values = [
    {
      icon: FiTarget,
      title: "Innovation First",
      description: "We constantly explore cutting-edge AI technologies to deliver solutions that push the boundaries of what's possible in software development."
    },
    {
      icon: FiHeart,
      title: "Quality Obsessed",
      description: "Every line of code is crafted with meticulous attention to detail, ensuring robust, secure, and high-performing applications."
    },
    {
      icon: FiUsers,
      title: "Client-Centric",
      description: "Your success is our success. We work as an extension of your team, understanding your goals and delivering solutions that drive real business value."
    },
    {
      icon: FiTrendingUp,
      title: "Growth Minded",
      description: "As a startup ourselves, we understand the importance of scalable solutions that grow with your business needs."
    }
  ];

  const approach = [
    {
      icon: FiCode,
      title: "Understand",
      description: "Deeply analyze your requirements and business goals"
    },
    {
      icon: FiBrain,
      title: "Innovate",
      description: "Design intelligent, AI-enhanced solutions"
    },
    {
      icon: FiZap,
      title: "Accelerate",
      description: "Utilize AI for faster coding, testing, and deployment"
    },
    {
      icon: FiTarget,
      title: "Optimize",
      description: "Continuously refine and improve performance and efficiency"
    },
    {
      icon: FiHeart,
      title: "Deliver",
      description: "Provide exceptional applications that drive real business value"
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="py-16 bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0, y: 40}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.8}}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              About aiximo
            </h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              Pioneering the Future of Software Development
            </p>
          </motion.div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0, y: 40}}
            whileInView={{opacity: 1, y: 0}}
            viewport={{once: true}}
            transition={{duration: 0.8}}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">Our Vision</h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              At <strong>aiximo</strong>, we envision a future where innovation is boundless, and development constraints are a thing of the past. Our mission is to become a global leader in AI-assisted development, empowering businesses of all sizes to achieve their digital ambitions with unprecedented speed, quality, and cost-efficiency.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              As a dynamic startup, we are driven by the belief that intelligently integrating AI into the development lifecycle can unlock new possibilities and redefine what's achievable in software engineering.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0, y: 40}}
            whileInView={{opacity: 1, y: 0}}
            viewport={{once: true}}
            transition={{duration: 0.8}}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Values</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              The principles that guide everything we do at aiximo
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{opacity: 0, y: 40}}
                whileInView={{opacity: 1, y: 0}}
                viewport={{once: true}}
                transition={{duration: 0.6, delay: index * 0.1}}
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center mb-6">
                  <SafeIcon icon={value.icon} className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Commitment Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{opacity: 0, x: -40}}
              whileInView={{opacity: 1, x: 0}}
              viewport={{once: true}}
              transition={{duration: 0.8}}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Our Commitment to Quality
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                Quality is the cornerstone of everything we do. We combine the rigorous standards of traditional software development with the transformative power of AI to deliver solutions that are not only fast but also robust, secure, and meticulously crafted.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Our expert team, leveraging advanced prompt engineering and state-of-the-art AI tools, is dedicated to ensuring every line of code contributes to a superior, high-performing application. <strong>Your success is our ultimate metric.</strong>
              </p>
            </motion.div>

            <motion.div
              initial={{opacity: 0, x: 40}}
              whileInView={{opacity: 1, x: 0}}
              viewport={{once: true}}
              transition={{duration: 0.8}}
              className="bg-gradient-to-br from-blue-50 to-purple-50 p-8 rounded-2xl"
            >
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">99.9%</div>
                  <div className="text-sm text-gray-600">Uptime Guarantee</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-600 mb-2">50%</div>
                  <div className="text-sm text-gray-600">Faster Development</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">24/7</div>
                  <div className="text-sm text-gray-600">Support Available</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-600 mb-2">100+</div>
                  <div className="text-sm text-gray-600">Projects Delivered</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0, y: 40}}
            whileInView={{opacity: 1, y: 0}}
            viewport={{once: true}}
            transition={{duration: 0.8}}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Our Approach</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
              We blend human expertise with AI intelligence. Our team of seasoned developers, prompt engineers, and project managers work synergistically with AI tools to deliver exceptional results.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-8">
            {approach.map((step, index) => (
              <motion.div
                key={index}
                initial={{opacity: 0, y: 40}}
                whileInView={{opacity: 1, y: 0}}
                viewport={{once: true}}
                transition={{duration: 0.6, delay: index * 0.1}}
                className="text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={step.icon} className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-600 text-sm">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{opacity: 0, y: 40}}
            whileInView={{opacity: 1, y: 0}}
            viewport={{once: true}}
            transition={{duration: 0.8}}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Join Us on This Journey
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Let's reshape the digital world together, one AI-accelerated project at a time. Partner with aiximo and experience the future of software development today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleStartProject}
                className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              >
                Start Your Project
              </button>
              <button
                onClick={handleLearnMore}
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-blue-600 transition-all duration-300"
              >
                Learn More
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;